import type { TenderLifecycleStatus, TenderStatus } from '@/types/domain';
import { daysUntil } from './dates';

/** Mirrors SQL function public.effective_tender_status(). */
export function deriveTenderStatus(
  lifecycle: TenderLifecycleStatus,
  endDate: string | null | undefined,
  thresholdDays: number,
): TenderStatus {
  if (lifecycle !== 'active') return lifecycle;
  const days = daysUntil(endDate);
  if (days == null) return 'active';
  if (days < 0) return 'expired';
  if (days <= thresholdDays) return 'expiring_soon';
  return 'active';
}

export interface StatusMeta {
  label: string;
  /** Tailwind classes for badge (bg, text, ring) */
  badge: string;
  /** Solid color used for dots / chart marks */
  color: string;
  description: string;
}

/**
 * The single visual definition of each status. Every badge, legend and chart uses this map,
 * so the treatment is consistent across the application.
 */
export const TENDER_STATUS_META: Record<TenderStatus, StatusMeta> = {
  draft:         { label: 'Draft',         badge: 'bg-slate-100 text-slate-700 ring-slate-300',     color: '#94a3b8', description: 'Being prepared; not yet awarded' },
  active:        { label: 'Active',        badge: 'bg-emerald-50 text-emerald-800 ring-emerald-300', color: '#059669', description: 'Contract in force' },
  expiring_soon: { label: 'Expiring Soon', badge: 'bg-amber-50 text-amber-800 ring-amber-300',       color: '#d97706', description: 'Ends within the alert window' },
  expired:       { label: 'Expired',       badge: 'bg-red-50 text-red-800 ring-red-300',             color: '#dc2626', description: 'End date passed; not yet closed' },
  completed:     { label: 'Completed',     badge: 'bg-blue-50 text-blue-800 ring-blue-300',          color: '#2563eb', description: 'Fulfilled and closed' },
  terminated:    { label: 'Terminated',    badge: 'bg-rose-100 text-rose-900 ring-rose-400',         color: '#9f1239', description: 'Ended early by the organization' },
  cancelled:     { label: 'Cancelled',     badge: 'bg-zinc-100 text-zinc-600 ring-zinc-300',         color: '#52525b', description: 'Withdrawn before award' },
};

export const TENDER_STATUS_ORDER: TenderStatus[] = [
  'draft', 'active', 'expiring_soon', 'expired', 'completed', 'terminated', 'cancelled',
];

export const LIFECYCLE_STATUSES: TenderLifecycleStatus[] = ['draft', 'active', 'completed', 'terminated', 'cancelled'];
